import { Link, useLocation, useParams, useSearchParams } from 'react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useKruimelNamen } from '@/stores/kruimelNamenStore';
import { useProductDetail } from '../hooks/useCompleteness';
import { useClientId } from '@/features/value/hooks/useClientId';
import { ScoreBar } from '@/components/ui/ScoreBar';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { GtinDetail } from '../components/GtinDetail';
import { CategoryBreakdown, CategoryBreakdownSkeleton } from '../components/CategoryBreakdown';
import { ProductImage } from '../components/ProductImage';
import { CalculatedAtCaption } from '@/features/data-quality/components/CalculatedAtCaption';
import { EvaluateProductButton } from '@/features/data-quality/components/EvaluateProductButton';

export function ProductDetailPage() {
  const { t } = useTranslation();
  const { clientId: routeClientId, productId } = useParams<{ clientId: string; productId: string }>();
  const [searchParams] = useSearchParams();
  const activeCategoryId = searchParams.get('category') ?? null;
  const resolvedClientId = useClientId();
  // Terug naar de lijst van herkomst (alleen een intern pad), anders de lijst van deze route (story 9).
  const terugNaar = (useLocation().state as { terugNaar?: unknown } | null)?.terugNaar;
  const terug = typeof terugNaar === 'string' && /^\/(?![/\\])/.test(terugNaar)
    ? terugNaar
    : routeClientId ? `/data-quality/${routeClientId}/products` : '/products';
  const { data, isLoading, isError, error, refetch } = useProductDetail(
    resolvedClientId,
    productId ?? '',
  );

  const product = data?.data;

  // Het kruimelpad kent alleen het adres, en daarin staat de ruwe productsleutel. Dit scherm kent
  // de naam wél, dus het geeft hem door zodra hij binnen is.
  const onthoud = useKruimelNamen((stand) => stand.onthoud);
  useEffect(() => {
    if (productId && product?.productName) onthoud(productId, product.productName);
  }, [productId, product?.productName, onthoud]);

  return (
    <div data-testid="product-detail-page">
      {/* Terug naar de lijst. Zonder deze knop kon dat alleen via het kruimelpad. */}
      <Link
        to={terug}
        data-testid="back-to-products"
        className="mb-3 inline-flex items-center gap-1 text-[0.8125rem] font-medium"
        style={{ color: 'var(--secondary)' }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: '18px' }} aria-hidden="true">
          arrow_back
        </span>
        {t('products.backToList')}
      </Link>
      {/* Loading */}
      {isLoading && (
        <div className="space-y-5">
          <div
            data-testid="product-detail-skeleton"
            className="rounded-xl p-5"
            style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)' }}
          >
            <div className="h-6 w-64 rounded animate-pulse mb-3" style={{ backgroundColor: 'var(--border)' }} />
            <div className="h-2 w-48 rounded animate-pulse" style={{ backgroundColor: 'var(--border)' }} />
          </div>
          <CategoryBreakdownSkeleton />
        </div>
      )}

      {/* Error */}
      {isError && <ApiErrorMessage error={error} onRetry={() => refetch()} />}

      {/* Content */}
      {product && (
        <div className="space-y-5">
          {/* Product header */}
          <div
            className="rounded-xl p-5"
            style={{
              backgroundColor: 'var(--background)',
              border: '1px solid var(--border-soft)',
              boxShadow: 'var(--card-shadow)',
            }}
          >
            <div className="flex items-start gap-5">
            {/* De bewaking hoort op `gtins` te staan, niet pas op het eerste element: een product
                zonder handelseenheden legde anders het hele scherm plat met "Cannot read
                properties of undefined (reading '0')". Gemeten 27-08-2026 — derde geval van
                dezelfde vorm in dit portaal. Verderop stond de controle al wél goed. */}
            <ProductImage gln={resolvedClientId} gtin={product.gtins?.[0]?.gtin ?? ''} productName={product.productName} variant="web" />
            <div>
            <h1
              data-testid="product-detail-name"
              className="text-[1.25rem] font-bold mb-2"
              style={{ color: 'var(--foreground-heading)', letterSpacing: '-0.02em' }}
            >
              {product.productName}
            </h1>
            <ScoreBar score={product.overallScore} variant="full" className="max-w-md" data-testid="product-detail-score-bar" />
            <CalculatedAtCaption isoDate={product.lastUpdated} className="mt-1" />
            </div>
            </div>
            {/* h4-2/FR26: on-demand herberekening — internal-only (employee/admin), never for a customer viewing their own product. */}
            {routeClientId && (
              <div className="mt-4">
                <EvaluateProductButton gln={routeClientId} cinXmlId={product.cinXmlId} />
              </div>
            )}
          </div>

          {/* Packaging layers (per-verpakkingslaag completeness) */}
          {product.gtins && product.gtins.length > 0 && (
            <div>
              <h2
                className="mb-3 text-[0.8125rem] font-semibold"
                style={{ color: 'var(--foreground-heading)' }}
              >
                {t('products.detail.packagingLayers')}
              </h2>
              <GtinDetail gtins={product.gtins} />
            </div>
          )}

          {/* Category breakdown sections */}
          {product.categories && (
            <div>
              <h2
                className="mb-3 text-[0.8125rem] font-semibold"
                style={{ color: 'var(--foreground-heading)' }}
              >
                {t('products.detail.categoryBreakdown')}
              </h2>
              <CategoryBreakdown
                categories={product.categories}
                activeCategoryId={activeCategoryId}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
